import { useState } from "react";
import { FaCheck, FaUndo, FaPalette } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
    THEME_PRESETS,
    getSavedTheme,
    applyTheme,
    saveTheme,
    applyAndSaveTheme
} from "../utils/theme";

import "./ManageAdmins.css";
import "./ThemeSettings.css";

function ThemeSettings() {

    const [current, setCurrent] = useState(getSavedTheme());

    const [customHex, setCustomHex] = useState(current.primary || "#2563eb");

    const [saving, setSaving] = useState(false);

    const persist = async (theme) => {

        setSaving(true);

        const savedToServer = await applyAndSaveTheme(theme);

        setSaving(false);

        if (!savedToServer) {

            toast.warning("Theme applied here, but couldn't be saved to your account - it may not follow you to other devices. Check your connection and try again.");

        }

        return savedToServer;

    };

    const handlePickPreset = async (preset) => {

        setCurrent(preset);

        setCustomHex(preset.primary);

        const ok = await persist(preset);

        if (ok) {

            toast.success(`Theme set to ${preset.label}.`);

        }

    };

    const handleCustomColorChange = async (e) => {

        const hex = e.target.value;

        setCustomHex(hex);

        // Derive shades the same way applyCustomColor used to -
        // inlined here since we need the theme object immediately for
        // setCurrent, before the (now async) save completes.
        const clean = hex.replace("#", "");
        const r = parseInt(clean.substring(0, 2), 16);
        const g = parseInt(clean.substring(2, 4), 16);
        const b = parseInt(clean.substring(4, 6), 16);
        const darken = (v) => Math.max(0, Math.round(v * 0.8));
        const lighten = (v) => Math.min(255, Math.round(v + (255 - v) * 0.85));
        const toHex = (v) => v.toString(16).padStart(2, "0");
        const theme = {
            key: "custom",
            label: "Custom",
            primary: hex,
            primaryDark: `#${toHex(darken(r))}${toHex(darken(g))}${toHex(darken(b))}`,
            primaryLight: `#${toHex(lighten(r))}${toHex(lighten(g))}${toHex(lighten(b))}`
        };

        setCurrent(theme);

        await persist(theme);

    };

    const handleReset = async () => {

        setCurrent(THEME_PRESETS[0]);

        setCustomHex(THEME_PRESETS[0].primary);

        applyTheme(THEME_PRESETS[0]);

        saveTheme(THEME_PRESETS[0]);

        const ok = await persist(THEME_PRESETS[0]);

        if (ok) {

            toast.success("Reset to the default theme.");

        }

    };

    return (

        <div className="page">

            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                pauseOnHover
            />

            <div className="page-header">

                <div>
                    <h2><FaPalette /> Theme Settings</h2>
                    <p className="page-subtitle">
                        Changes apply immediately and are saved to your account - your theme
                        follows you wherever you log in, without affecting anyone else's. Affects
                        the primary action color used for buttons, focus states, and highlights
                        across every admin_UI page.
                    </p>
                </div>

                <button type="button" className="add-btn" onClick={handleReset} disabled={saving}>
                    <FaUndo /> Reset to Default
                </button>

            </div>

            <div className="inline-form-container">

                <div className="inline-form-header">
                    <h3>Presets</h3>
                </div>

                <div className="theme-preset-grid">

                    {THEME_PRESETS.map(preset => (

                        <button
                            type="button"
                            key={preset.key}
                            className={`theme-preset-swatch ${current.key === preset.key ? "selected" : ""}`}
                            onClick={() => handlePickPreset(preset)}
                            title={preset.label}
                            disabled={saving}
                        >

                            <span
                                className="theme-preset-color"
                                style={{ background: preset.primary }}
                            >
                                {current.key === preset.key && <FaCheck />}
                            </span>

                            <span className="theme-preset-label">{preset.label}</span>

                        </button>

                    ))}

                </div>

                <div className="inline-form-header" style={{ marginTop: "24px" }}>
                    <h3>Custom Color</h3>
                </div>

                <div className="theme-custom-row">

                    <input
                        type="color"
                        value={customHex}
                        onChange={handleCustomColorChange}
                        className="theme-custom-picker"
                        disabled={saving}
                    />

                    <span className="theme-custom-hex">{customHex}</span>

                    <span className="theme-custom-hint">
                        Pick any color - a matching hover shade and light tint are generated automatically.
                        {saving && " Saving..."}
                    </span>

                </div>

                <div className="inline-form-header" style={{ marginTop: "24px" }}>
                    <h3>Live Preview</h3>
                </div>

                <div className="theme-preview-row">

                    <button type="button" className="add-btn" onClick={(e) => e.preventDefault()}>
                        Sample Button
                    </button>

                    <input placeholder="Sample input (click to see focus color)" className="theme-preview-input" />

                </div>

            </div>

        </div>

    );

}

export default ThemeSettings;
